import { TypedEmitter } from 'tiny-typed-emitter'
import { AudioEngineDevSettings } from '../../AppContext'
import { AudioSourceInfo, AudioInputSettings, AudioTrackWithInfo, AudioOutputSettings } from '../../UserContext'
import { AudioEngine } from './AudioEngine'
import FakeMediaStreamTrack from './FakeMediaStreamTrack'
import { HybridAudioEngineRoom } from './HybridAudioEngineRoom'
import { AudioEngineAudioDeviceInfo, AudioEngineOutputDeviceType } from './types'
import { IAudioEngine, AudioEngineEventEmitter, AudioEngineImpl } from './IAudioEngine'
import { IAudioEngineRoom } from './IAudioEngineRoom'
import { WebAudioEngine } from './WebAudioEngine'

export class HybridAudioEngine extends TypedEmitter<AudioEngineEventEmitter> implements IAudioEngine {
    private audioEngine: AudioEngine
    private webAudioEngine: WebAudioEngine

    constructor() {
        super()
        this.audioEngine = new AudioEngine()
        this.webAudioEngine = new WebAudioEngine()

        const audioEngineReady = new Promise<void>((r) => this.audioEngine.on('onInitializationSuccess', () => r()))
        const webAudioEngineReady = new Promise<void>((r) =>
            this.webAudioEngine.on('onInitializationSuccess', () => r())
        )

        this.audioEngine.on('onInitializationSuccess', () => this.emit('onInitializationSuccess'))
        this.audioEngine.on('onFatalError', (err) => this.emit('onFatalError', err))
        this.audioEngine.on('onNetworkStatusChanged', (s) => this.emit('onNetworkStatusChanged', s))
        this.audioEngine.on('networkStatsEvent', (s) => this.emit('networkStatsEvent', s))

        this.webAudioEngine.on('onFatalError', (err) => this.emit('onFatalError', err))
        this.webAudioEngine.on('onNetworkStatusChanged', (s) => this.emit('onNetworkStatusChanged', s))

        Promise.all([audioEngineReady, webAudioEngineReady]).then(() => this.emit('onInitializationSuccess'))
    }

    async init() {
        await Promise.all([this.audioEngine.init(), this.webAudioEngine.init()])
    }

    createRoom(): IAudioEngineRoom {
        const audioEngineRoom = this.audioEngine.createRoom()
        const webAudioEngineRoom = this.webAudioEngine.createRoom()

        return new HybridAudioEngineRoom(audioEngineRoom, webAudioEngineRoom)
    }

    getCurrentRoom(): IAudioEngineRoom | null {
        const audioEngineRoom = this.audioEngine.getCurrentRoom()
        const webAudioEngineRoom = this.webAudioEngine.getCurrentRoom()

        if (audioEngineRoom === null || webAudioEngineRoom === null) {
            return null
        }
        return new HybridAudioEngineRoom(audioEngineRoom, webAudioEngineRoom)
    }

    async setSettings(settings: AudioEngineDevSettings, authToken?: string | undefined): Promise<boolean> {
        const [r1, r2] = await Promise.all([
            this.audioEngine.setSettings(settings, authToken),
            this.webAudioEngine.setSettings(settings, authToken),
        ])
        return r1 && r2
    }

    getAudioDevices(): Promise<AudioEngineAudioDeviceInfo[] | null> {
        return this.audioEngine.getAudioDevices()
    }

    checkSupportedSampleRates(params: {
        audioEngineDeviceId: string
        sampleRates: number[]
        deviceDirection: 'input' | 'output'
    }): Promise<number[]> {
        return this.audioEngine.checkSupportedSampleRates(params)
    }

    getAudioStream(
        source: AudioSourceInfo,
        settings: AudioInputSettings
    ): Promise<{ track: AudioTrackWithInfo; modifiedSettings: Partial<AudioInputSettings> }> {
        return this.audioEngine.getAudioStream(source, settings)
    }

    startRemoteRecording(
        streamId: string,
        channelGroup: number | null,
        outputFileFullPath: string | null,
        source: AudioSourceInfo | null,
        settings: AudioOutputSettings | null
    ): void {
        this.webAudioEngine.startRemoteRecording(streamId, channelGroup, outputFileFullPath, source, settings)
    }

    stopRemoteRecording(streamId: string, channelGroup: number) {
        this.webAudioEngine.stopRemoteRecording(streamId, channelGroup)
    }

    startLocalRecording(streamId: string, filePath: string, channelGroup?: number | undefined): void {
        this.audioEngine.startLocalRecording(streamId, filePath, channelGroup)
    }

    stopLocalRecording(streamId: string, channelGroup?: number | undefined): void {
        this.audioEngine.stopLocalRecording(streamId, channelGroup)
    }

    async setAudioOutputDevice(
        source: AudioSourceInfo | null,
        settings: AudioOutputSettings | null,
        outputIdx?: AudioEngineOutputDeviceType
    ): Promise<boolean> {
        const nativeResult = this.audioEngine.setAudioOutputDevice(source, settings, outputIdx)
        const webResult = this.webAudioEngine.setAudioOutputDevice(source, settings, outputIdx)
        const [r1, r2] = await Promise.all([nativeResult, webResult])
        return r1 && r2
    }

    setAudioLoopbackStream(
        track: AudioTrackWithInfo | FakeMediaStreamTrack,
        outputIdx?: number | undefined
    ): Promise<boolean> {
        return this.audioEngine.setAudioLoopbackStream(track, outputIdx)
    }

    unsetAudioLoopbackStream(track: AudioTrackWithInfo): Promise<boolean> {
        return this.audioEngine.unsetAudioLoopbackStream(track)
    }

    startMasterCaptureToFile(filePath: string): Promise<boolean> {
        // NOTE: Incoming audio engine streams are captured via the
        // masterStreamNode along with other webRTC streams. If necessary the
        // masterStreamNode could be connected to all worklets here, then
        // disconnected in stopMasterCaptureToFile, but this optimzation is
        // probably unnecessary.

        return this.audioEngine.startMasterCaptureToFile(filePath)
    }

    stopMasterCaptureToFile(): Promise<boolean> {
        return this.audioEngine.stopMasterCaptureToFile()
    }

    quit(): void {
        this.audioEngine.quit()
        this.webAudioEngine.quit()
    }

    testCrash(): Promise<boolean> {
        return this.audioEngine.testCrash()
    }

    get implementation(): AudioEngineImpl {
        return 'hybrid'
    }

    get masterStreamNode(): MediaStreamAudioDestinationNode {
        return this.webAudioEngine.masterStreamNode
    }
}
